import React, { useEffect, useState } from 'react';
import {
  FaEdit,
  FaPlus,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp
} from 'react-icons/fa';
import { useAuth } from '../../provider/auth.provider';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import { Pagination } from '../../utils/pagination.utils';
import { Department, DepartmentPagination } from './types';
import { formatHolidayDate, holidayLabel } from './holiday_select';

const MAX_HOLIDAY_CHIPS = 4;

interface DepartmentsListProps {
  departments: Department[];
  pagination: DepartmentPagination;
  onSearch: (search: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onAddDepartment: () => void;
  onEditDepartment: (department: Department) => void;
  onSort: (field: string, direction: 'ASC' | 'DESC') => void;
  loading?: boolean;
  searchValue?: string;
  sortState?: { field: string; direction: 'ASC' | 'DESC' };
}

export const DepartmentsList: React.FC<DepartmentsListProps> = ({
  departments,
  pagination,
  onSearch,
  onPageChange,
  onLimitChange,
  onAddDepartment,
  onEditDepartment,
  onSort,
  loading = false,
  searchValue = '',
  sortState
}) => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState(searchValue);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>(
    sortState || { field: 'name', direction: 'ASC' }
  );

  const debouncedOnSearch = useDebouncedCallback(onSearch, 400);

  useEffect(() => {
    setSearchTerm(searchValue || '');
  }, [searchValue]);

  useEffect(() => {
    if (sortState) {
      setSortConfig(sortState);
    }
  }, [sortState]);

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    setSortConfig({ field, direction });
    onSort(field, direction);
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />;
    return sortConfig.direction === 'ASC' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />;
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">Departments Management</h2>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {hasPermission('Department', 'create') && (
              <button
                onClick={onAddDepartment}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add Department
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="max-w-md">
          <label className="block text-xs font-medium text-gray-700 mb-1">Search Departments</label>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => {
                const value = event.target.value;
                setSearchTerm(value);
                debouncedOnSearch(value);
              }}
              placeholder="Search by department name"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">
                Actions
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[220px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Department Name
                  {getSortIcon('name')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[280px]"
              >
                Projects
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[280px]">
                Holidays
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center gap-1">
                  Created At
                  {getSortIcon('created_at')}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('updated_at')}
              >
                <div className="flex items-center gap-1">
                  Updated At
                  {getSortIcon('updated_at')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3" />
                    <span className="text-sm text-gray-600">Loading departments...</span>
                  </div>
                </td>
              </tr>
            ) : departments.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <p className="text-sm text-gray-500">No departments found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search</p>
                  </div>
                </td>
              </tr>
            ) : (
              departments.map((department) => (
                <tr key={department.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    {hasPermission('Department', 'edit') && (
                      <button
                        onClick={() => onEditDepartment(department)}
                        className="text-blue-700 hover:text-blue-900 p-2 hover:bg-blue-50 rounded transition-colors"
                        title="Edit department"
                      >
                        <FaEdit className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{department.name}</span>
                  </td>
                  <td className="px-4 py-4">
                    {department.projects?.length ? (
                      <div className="flex flex-wrap gap-2">
                        {department.projects.map((project) => (
                          <span
                            key={project.id}
                            className="inline-flex items-center rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800"
                          >
                            {project.project_short_name || project.project_name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">No projects</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {department.holidays?.length ? (
                      <div
                        className="flex flex-wrap gap-2"
                        title={department.holidays.map(holidayLabel).join('\n')}
                      >
                        {department.holidays.slice(0, MAX_HOLIDAY_CHIPS).map((holiday) => (
                          <span
                            key={holiday.id}
                            className="inline-flex items-center rounded-full border border-purple-100 bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-800"
                          >
                            {holiday.holiday_name}
                            <span className="ml-1 text-purple-500">{formatHolidayDate(holiday.holiday_date)}</span>
                          </span>
                        ))}
                        {department.holidays.length > MAX_HOLIDAY_CHIPS && (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                            +{department.holidays.length - MAX_HOLIDAY_CHIPS} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 italic">No department-specific holidays</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-700">{formatDate(department.created_at)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-700">{formatDate(department.updated_at)}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />
    </div>
  );
};
